import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import messages from '../../../assets/messages'
import {OfferNode} from '../types'
import {buildContributionRows, ContributionsKind, contributionsKind} from '../utils/contributions'
import {formatKpiMoney, formatShare, NodeRole} from '../utils/kpiGrid'
import {
  EmptyState,
  FundsCell,
  HeadRow,
  OwnerCell,
  OwnerName,
  Row,
  RoleCell,
  ScrollArea,
  ScrollButton,
  Section,
  SectionHeading,
  Table,
  Td,
  Th,
  TotalLabel,
  TotalRow,
} from './ContributionsTable.styled'
import {useExpansion} from './ExpansionContext'

type KpiMessage = {i18nKey: string; defaults: string}

type ContributionsTableProps = {
  node: OfferNode
} & WithNamespaces

/** Heading that names the children's collective role (AC: adapts Agents / Investors / mixed). */
const HEADING: Record<ContributionsKind, KpiMessage> = {
  agents: messages.pammContribHeadingAgents,
  investors: messages.pammContribHeadingInvestors,
  mixed: messages.pammContribHeadingMixed,
}

/** The role term shown in the Role column. */
const ROLE_LABEL: Record<NodeRole, KpiMessage> = {
  master: messages.pammRoleMoneyManager,
  agent: messages.pammRoleAgent,
  investor: messages.pammRoleInvestor,
}

/**
 * The "Funds by Direct Agent/Investor" table on a node's KPI panel (PT-5714): one row per direct
 * child offer — Owner, Role, Funds, Share % and Clients — sorted largest-contributor-first so an
 * agent can see who deserves attention at a glance. Sub-agent rows are clickable and scroll the tree
 * to that child; a leaf investor row has no subtree to open and stays inert. A node with no direct
 * children shows an empty-state line instead of a bare table.
 *
 * Every figure comes from the children already in memory, so the table renders with no extra request.
 *
 * @param node - The node whose direct children the table describes.
 * @param t - Translation function injected by `withNamespaces`, used for the row-click aria label.
 */
const _ContributionsTable = ({node, t}: ContributionsTableProps) => {
  const currency = node.offer.currency ?? 'USD'
  const {revealAndScroll} = useExpansion()
  const rows = buildContributionRows(node)

  if (rows.length === 0) {
    return (
      <Section data-testid="kpi-contributions">
        <EmptyState data-testid="contributions-empty">
          <Trans {...messages.pammContribEmpty} />
        </EmptyState>
      </Section>
    )
  }

  const totalFunds = rows.reduce((sum, row) => sum + row.funds, 0)
  // The shares (by construction) total 100% whenever there are funds to split; with none, the
  // total is undefined too and renders "–" rather than a bogus 0%.
  const totalShare = rows.some((row) => row.share != null) ? 1 : null

  return (
    <Section data-testid="kpi-contributions">
      <SectionHeading>
        <Trans {...HEADING[contributionsKind(rows)]} />
      </SectionHeading>
      <ScrollArea>
        <Table>
          <thead>
            <HeadRow>
              <Th>
                <Trans {...messages.pammContribColOwner} />
              </Th>
              <Th>
                <Trans {...messages.pammContribColRole} />
              </Th>
              <Th align="right">
                <Trans {...messages.pammContribColFunds} />
              </Th>
              <Th align="right">
                <Trans {...messages.pammContribColShare} />
              </Th>
              <Th align="right">
                <Trans {...messages.pammContribColClients} />
              </Th>
            </HeadRow>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              // A sub-agent leads to a subtree worth navigating to; a leaf investor has nothing
              // beneath it, so only agent rows are made into scroll targets (AC).
              const scrollTarget = row.role === 'agent' ? row.offerId : null
              return (
                <Row
                  // Owner names are not unique and ids can be missing, so fold the index into the key.
                  key={`${row.offerId ?? 'unknown'}-${index}`}
                  data-testid={`contribution-row-${row.offerId ?? 'unknown'}`}
                  clickable={scrollTarget != null}
                  // Whole-row click is a mouse-only convenience; the accessible control is the button
                  // in the Owner cell, which carries the name and keyboard activation.
                  onClick={scrollTarget != null ? () => revealAndScroll(scrollTarget) : undefined}
                >
                  <OwnerCell>
                    {scrollTarget != null ? (
                      <ScrollButton
                        type="button"
                        data-testid={`contribution-scroll-${row.offerId}`}
                        title={row.ownerName}
                        aria-label={t(messages.pammContribScrollTo.i18nKey, messages.pammContribScrollTo.defaults, {
                          owner: row.ownerName,
                        })}
                        onClick={(event) => {
                          // The row also handles the click; stop it so the scroll fires exactly once.
                          event.stopPropagation()
                          revealAndScroll(scrollTarget)
                        }}
                      >
                        {row.ownerName}
                      </ScrollButton>
                    ) : (
                      <OwnerName title={row.ownerName}>{row.ownerName}</OwnerName>
                    )}
                  </OwnerCell>
                  <RoleCell>
                    <Trans {...ROLE_LABEL[row.role]} />
                  </RoleCell>
                  <FundsCell align="right">{formatKpiMoney(row.funds, currency)}</FundsCell>
                  <Td align="right">{formatShare(row.share)}</Td>
                  <Td align="right">{row.clients}</Td>
                </Row>
              )
            })}
          </tbody>
          <tfoot>
            <TotalRow data-testid="contribution-total">
              <TotalLabel>
                <Trans {...messages.pammContribTotal} />
              </TotalLabel>
              <td />
              <td style={{textAlign: 'right'}}>{formatKpiMoney(totalFunds, currency)}</td>
              <td style={{textAlign: 'right'}}>{formatShare(totalShare)}</td>
              <td />
            </TotalRow>
          </tfoot>
        </Table>
      </ScrollArea>
    </Section>
  )
}

export const ContributionsTable = withNamespaces()(_ContributionsTable)
